import React, { useState, useEffect } from 'react';
import './SundeepInvoiceMapping.css';
import API_BASE_URL from '../config';

const SundeepInvoiceMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMapping, setEditingMapping] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  // Form state for editing
  const [formData, setFormData] = useState({
    igd_sku_number: '',
    sundeep_item_code: '',
    sundeep_invoice_description: ''
  });

  // Fetch mappings from the backend
  const fetchMappings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/sundeep-invoice-mapping`);
      if (!response.ok) {
        throw new Error('Failed to fetch mappings');
      }
      const data = await response.json();
      setMappings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      igd_sku_number: '',
      sundeep_item_code: '',
      sundeep_invoice_description: ''
    });
    setEditingMapping(null);
    setShowEditForm(false);
  };

  // Update existing mapping
  const handleUpdateMapping = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/sundeep-invoice-mapping/${editingMapping.igd_sku_number}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update mapping');
      }

      await fetchMappings();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing mapping
  const handleEditMapping = (mapping) => {
    setFormData({
      igd_sku_number: mapping.igd_sku_number || '',
      sundeep_item_code: mapping.sundeep_item_code || '',
      sundeep_invoice_description: mapping.sundeep_invoice_description || ''
    });
    setEditingMapping(mapping);
    setShowEditForm(true);
  };

  // Filter and search mappings
  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = 
      mapping.igd_sku_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.sundeep_item_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.sundeep_invoice_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mapping.igd_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="sundeep-invoice-mapping">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading mapping data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sundeep-invoice-mapping">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Sundeep Invoice Mapping</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Map Sundeep item codes to IGD SKU numbers</li>
              <li>Edit existing mappings for invoice processing</li>
              <li>View IGD product names for reference</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <span>❌ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="controls-section">
          <div className="search-filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search mappings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon">🔍</span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        {showEditForm && (
          <div className="form-overlay">
            <div className="form-container">
              <div className="form-header">
                <h2>Edit Mapping</h2>
                <button className="close-button" onClick={resetForm}>×</button>
              </div>
              
              <form onSubmit={handleUpdateMapping}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>IGD SKU Number</label>
                    <input
                      type="text"
                      name="igd_sku_number"
                      value={formData.igd_sku_number}
                      disabled
                      className="disabled-input"
                    />
                    <div className="info-message">This field cannot be edited</div>
                  </div>

                  <div className="form-group">
                    <label>IGD Name</label>
                    <input
                      type="text"
                      value={editingMapping?.igd_name || ''}
                      disabled
                      className="disabled-input"
                    />
                    <div className="info-message">Read-only reference field</div>
                  </div>

                  <div className="form-group">
                    <label>Sundeep Item Code</label>
                    <input
                      type="text"
                      name="sundeep_item_code"
                      value={formData.sundeep_item_code}
                      onChange={handleInputChange}
                      placeholder="Enter Sundeep item code"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Sundeep Invoice Description</label>
                    <textarea
                      name="sundeep_invoice_description"
                      value={formData.sundeep_invoice_description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter Sundeep invoice description"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={resetForm} className="cancel-button">
                    Cancel
                  </button>
                  <button type="submit" className="save-button">
                    Update Mapping
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Mappings Table */}
        <div className="table-container">
          <table className="mapping-table">
            <thead>
              <tr>
                <th>IGD SKU Number</th>
                <th>IGD Name</th>
                <th>Sundeep Item Code</th>
                <th>Sundeep Invoice Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    {searchTerm ? 'No mappings match your search criteria' : 'No mappings found'}
                  </td>
                </tr>
              ) : (
                filteredMappings.map(mapping => (
                  <tr key={mapping.igd_sku_number}>
                    <td className="sku-number">{mapping.igd_sku_number}</td>
                    <td className="sku-name">{mapping.igd_name || '-'}</td>
                    <td className="item-code">{mapping.sundeep_item_code || '-'}</td>
                    <td className="description">{mapping.sundeep_invoice_description || '-'}</td>
                    <td className="actions">
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEditMapping(mapping)}
                        title="Edit Mapping"
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="summary">
          <p>Showing {filteredMappings.length} of {mappings.length} mappings</p>
        </div>
      </div>
    </div>
  );
};

export default SundeepInvoiceMapping; 